import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { delay, filter, Subscription, take } from 'rxjs';

import { WEEK_DAYS } from '@shared/schedule/constants';
import { timepickerValidator } from '../../timepicker-validator';
import {
  IDailySchedule,
  ISchedule,
  WeekDays,
} from '../../models';
import { scheduleValidator } from '../../schedule-validator';
import { CloseDayScheduleComponent } from '../close-day-schedule/close-day-schedule.component';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';

@Component({
  selector: 'tablesready-add-edit-schedule',
  templateUrl: './add-edit-schedule.component.html',
  styleUrls: ['./add-edit-schedule.component.scss'],
})
export class AddEditScheduleComponent implements OnInit, OnDestroy {
  @Input() schedule: ISchedule;
  @Input() schedules: ISchedule[];
  icon = AppIcon;
  title: string;
  daysInWeek: string[] = WEEK_DAYS;
  hours: Record<WeekDays, IDailySchedule>;
  splitHours: boolean;
  secondarySchedulesName: string;

  schedulesForm: UntypedFormGroup;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly fb: UntypedFormBuilder,
  ) { }

  ngOnInit() {
    let isNewSchedule = false;
    this.title = Object.keys(this.schedule).length === 0 ? 'Add' : 'Edit';

    if (Object.keys(this.schedule).length === 0) {
      isNewSchedule = true;
      const defaultSchedule = this.schedules.find(schedule => schedule.default);
      const emptySchedule = this.daysInWeek.reduce((obj, key) => {
        const defaultScheduleHours = defaultSchedule.hours[key];
        return {
          ...obj,
          [key]: {
            hours: defaultScheduleHours.hours,
            closed: defaultScheduleHours.closed,
            nextDay: defaultScheduleHours.nextDay,
            publicDisabled: defaultScheduleHours.publicDisabled,
          },
        };
      }, {});

      this.schedule = {
        name: null,
        hours: emptySchedule as {
          [key in WeekDays]: IDailySchedule
        },
        exceptions: defaultSchedule.exceptions,
      };
    }

    this.hours = this.schedule.hours;

    const splitHoursLength = 2;
    const allSchedules = isNewSchedule ? [...this.schedules, this.schedule] : this.schedules;
    const maxHoursLength = Object.values(this.hours).reduce((acc: number, day) => {
      return Math.max(acc, (day.hours || []).length);
    }, 0);
    this.splitHours = maxHoursLength === splitHoursLength;

    this.schedulesForm = this.fb.group({
      name: [this.schedule.name, [Validators.required]],
      hours: this.fb.group(this.fillFormIn()),
      restaurant: [this.schedule.restaurant],
      splitHours: [this.splitHours],
      _id: [this.schedule._id]
    }, {
      validators: [scheduleValidator(allSchedules)]
    });

    const statusChangeSubscription = this.schedulesForm.statusChanges.subscribe(status => {
      const errors = this.schedulesForm.errors;
      if (status === 'INVALID' && errors?.validDefaultSchedule) {
        this.secondarySchedulesName = this.schedules
          .filter((schedule, i) => errors.indices.includes(i))
          .map(schedule => schedule.name)
          .join(', ');
      }
    });
    this.subscriptions.add(statusChangeSubscription);
  }

  fillFormIn() {
    const fbObject = {};
    this.daysInWeek.forEach((day) => {
      fbObject[day] = this.fb.group({
        hoursFirstValueFirstArray: [this.schedule.hours[day].hours[0][0]],
        hoursSecondValueFirstArray: [this.schedule.hours[day].hours[0][1]],
        hoursFirstValueSecondArray: [this.check(day, 0)],
        hoursSecondValueSecondArray: [this.check(day, 1)],
        closed: [this.schedule.hours[day].closed],
        nextDay: [this.schedule.hours[day].nextDay],
        publicDisabled: [this.schedule.hours[day].publicDisabled || false],
      },
      {
        validators: [timepickerValidator],
      });
    });
    return fbObject;
  }

  check(day, position) {
    return this.schedule.hours[day].hours.length > 1 ? this.schedule.hours[day].hours[1][position] : '';
  }

  applyToAll() {
    this.daysInWeek.forEach((d) => {
      if (d !== 'Mon') {
        this.schedulesForm.get('hours.' + d).patchValue(this.schedulesForm.value.hours.Mon);
      }
    });
    this.schedulesForm.markAsDirty();
  }

  toggleSplitHours() {
    this.splitHours = !this.splitHours;
  }

  confirm(): void {
    const schedule = { ...this.schedule, ...this.schedulesForm.value };

    for (const property in schedule.hours) {
      if (Object.prototype.hasOwnProperty.call(schedule.hours, property)) {
        schedule.hours[property].hours = [
          [schedule.hours[property].hoursFirstValueFirstArray, schedule.hours[property].hoursSecondValueFirstArray]
        ];

        if (typeof schedule.hours[property].hoursFirstValueSecondArray !== 'string' ||
          typeof schedule.hours[property].hoursSecondValueSecondArray !== 'string') {
          schedule.hours[property].hours.push([schedule.hours[property].hoursFirstValueSecondArray, schedule.hours[property].hoursSecondValueSecondArray]);
        }

        if (!this.splitHours && schedule.hours[property].hours.length > 1) {
          schedule.hours[property].hours.pop();
        }

        delete schedule.hours[property].hoursFirstValueFirstArray;
        delete schedule.hours[property].hoursFirstValueSecondArray;
        delete schedule.hours[property].hoursSecondValueFirstArray;
        delete schedule.hours[property].hoursSecondValueSecondArray;
        delete schedule.splitHours;
      }
    }

    if (!schedule._id) {
      delete schedule._id;
      delete schedule.restaurant;
    }

    const isDefaultSchedule = schedule._id === this.schedules[0]._id;
    const originalSchedule = this.schedules.find(s => s._id === schedule._id);
    let closedAdded = false;
    let publicDisabledAdded = false;

    if (originalSchedule) {
      for (const prop in originalSchedule.hours) {
        if (!originalSchedule.hours[prop]?.closed && schedule.hours[prop]?.closed === true) {
          closedAdded = true;
          break;
        }

        if (!originalSchedule.hours[prop]?.publicDisabled && schedule.hours[prop]?.publicDisabled === true) {
          publicDisabledAdded = true;
          break;
        }
      }
    }

    if (!isDefaultSchedule && (closedAdded || publicDisabledAdded)) {
      this.modalController.create<CloseDayScheduleComponent, boolean>(
        CloseDayScheduleComponent,
        { closedType: publicDisabledAdded ? 'disable public' : 'closed' },
      )
        .pipe(
          take(1),
          filter(res => !!res),
          delay(100),
        )
        .subscribe(() => {
          this.modalController.dismiss(schedule);
        });
    } else {
      this.modalController.dismiss(schedule);
    }
  }

  cancel(): void {
    this.modalController.dismiss();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
